import React, {Dispatch, SetStateAction, useCallback, useContext} from "react";
import FlexBox from "view/components/Layout/FlexBox";
import {Button, Typography, useTheme} from "@material-ui/core";
import type {IGridSchema} from "ts/api/grid-schema";
import {Controller, UseFieldArrayRemove, UseFormReturn} from "react-hook-form";
import {grey} from "@material-ui/core/colors";
import DeleteIcon from "@material-ui/icons/Delete";
import AlertIcon from "view/images/AlertIcon";
import clsx from "clsx";
import TextareaAutosize from "react-textarea-autosize";
import {FormContext} from "view/components/Form";
import {ActiveCells, border, columnsName, indexToLetter} from "../../tools";
import useStyles from "./styles";

export type ColumnProps = {
  activeCells?: ActiveCells;
  setAreAllCellsMandatory?: Dispatch<SetStateAction<boolean>>;
  viewOnly?: boolean;
  cellsBeforeMarkingMandatory?: React.MutableRefObject<
    IGridSchema["cells"] | null
  >;
  /**
   * Loose shape — only `gridSchema` is consumed here, so wire-native
   * `IGetOneByIdTaskRes` and the FE form state both satisfy it.
   */
  task?: {gridSchema?: IGridSchema | null};
  isResponseMode?: boolean;
  removeColumnField?: UseFieldArrayRemove;
  /**
   * Resolved by the parent `Grid` (prop → live definition). `false` is the
   * "ONLY COLUMNS" mode, so it must win over the form value — hence `??` below,
   * never `||`.
   */
  includeRowTitles?: boolean;
};

function Columns({
  activeCells,
  setAreAllCellsMandatory,
  viewOnly,
  cellsBeforeMarkingMandatory,
  task,
  isResponseMode,
  removeColumnField,
  includeRowTitles: includeRowTitlesProp,
}: ColumnProps) {
  const classes = useStyles();
  const theme = useTheme();

  const form = useContext(FormContext) as UseFormReturn<{
    gridSchema?: IGridSchema;
  }>;

  const {watch, setValue, control} = form;

  const cells = task?.gridSchema?.cells || watch("gridSchema.cells");
  const columns = task?.gridSchema?.columns || watch("gridSchema.columns");
  const includeRowTitles =
    includeRowTitlesProp ??
    task?.gridSchema?.includeRowTitles ??
    watch("gridSchema.includeRowTitles");

  const deleteColumn = useCallback(
    (columnIndex: number) => {
      if (!cells || !removeColumnField) {
        return;
      }
      removeColumnField(columnIndex);
      const copyCells = {...cells};
      const cellEntries = Object.entries(copyCells);

      for (let i = 0; i < cellEntries.length; i += 1) {
        copyCells[i].splice(columnIndex, 1);
      }
      if (cellsBeforeMarkingMandatory) {
        cellsBeforeMarkingMandatory.current = null;
      }
      setValue("gridSchema.cells", copyCells, {shouldDirty: true});
    },
    [cells]
  );

  const makeColumnMandatory = useCallback(
    (columnIndex: number) => {
      if (!cells) {
        return;
      }
      const copyCells = {...cells};
      const cellEntries = Object.entries(copyCells);

      const [mandatories, nonMandatories] = cellEntries?.reduce<
        [number, number]
      >(
        (acc, [_, row]) =>
          row[columnIndex]?.required
            ? [acc[0] + 1, acc[1]]
            : [acc[0], acc[1] + 1],
        [0, 0]
      );
      const isRequired = mandatories < nonMandatories;
      for (let i = 0; i < cellEntries.length; i += 1) {
        copyCells[i][columnIndex].required = isRequired;
      }
      if (!isRequired && setAreAllCellsMandatory) {
        setAreAllCellsMandatory(false);
      }
      if (cellsBeforeMarkingMandatory) {
        cellsBeforeMarkingMandatory.current = null;
      }
      setValue("gridSchema.cells", copyCells, {shouldDirty: true});
    },
    [cells]
  );

  const isDeleteDisabled = columns.length <= 1;

  return (
    <>
      {columns.map((col, index) => {
        const isChosenColumn = activeCells?.columnIndex === index;
        const isActive =
          (isChosenColumn &&
            (activeCells?.rowIndex === 0 || activeCells?.isFullColumn)) ||
          (activeCells?.rowIndex === 0 && activeCells?.isFullRow);
        const name = `${columnsName}.${index}.name` as const;
        const shouldShowLeftBorder = !includeRowTitles && index === 0;
        return (
          <FlexBox
            key={index}
            className={classes.columnHeadCell}
            column
            alignCenter
          >
            {!viewOnly && !isResponseMode && (
              <Typography
                style={{
                  color: isChosenColumn
                    ? theme.palette.primary.main
                    : undefined,
                  fontWeight: isChosenColumn ? 500 : undefined,
                }}
                className={classes.index}
              >
                {indexToLetter[index] || indexToLetter.default}
              </Typography>
            )}
            <FlexBox
              style={{
                borderBottom: isActive ? border() : undefined,
              }}
              className={clsx(
                classes.headCell,
                index === columns.length - 1 && classes.lastHeadCell,
                shouldShowLeftBorder && classes.leftBorder
              )}
              alignCenter
            >
              <Controller
                control={control}
                render={({field: props}) => {
                  const titleValue =
                    columns?.[index]?.name || (props.value as string);
                  return (
                    <TextareaAutosize
                      onKeyDown={(e) => {
                        if (e.shiftKey && e.key === "Enter") {
                          e.preventDefault();
                          e.stopPropagation();
                          const curRow = `${columns?.[index]?.name || ""}\n`;
                          return setValue(name, curRow, {
                            shouldDirty: true,
                          });
                        }
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.stopPropagation();
                          (e.target as HTMLElement)?.blur();
                        }
                      }}
                      onChange={(e) => {
                        const {value} = e.target;
                        setValue(name, value, {
                          shouldDirty: true,
                        });
                      }}
                      disabled={viewOnly || isResponseMode}
                      defaultValue={col.name}
                      value={titleValue}
                      className={clsx(
                        classes.headCellInput,
                        !titleValue?.trim() && classes.headCellInputEmptyValue
                      )}
                    />
                  );
                }}
                name={name as any}
              />
              {!viewOnly && !isResponseMode && (
                <>
                  <Button
                    className={classes.makeColumnMandatoryButton}
                    onClick={() => makeColumnMandatory(index)}
                  >
                    <AlertIcon />
                  </Button>
                  <Button
                    disabled={isDeleteDisabled}
                    className={classes.deleteColumnButton}
                    onClick={() => deleteColumn(index)}
                  >
                    <DeleteIcon
                      className={clsx(
                        classes.icon,
                        isDeleteDisabled && classes.disableDeleteColumnSVG
                      )}
                      htmlColor={grey[500]}
                    />
                  </Button>
                </>
              )}
            </FlexBox>
          </FlexBox>
        );
      })}
    </>
  );
}

export default Columns;
